import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { Building2, User, Settings, Check, ArrowRight, ArrowLeft } from 'lucide-react';

export default function CompanyCreationInterface() {
  const { userProfile, loading, createCompany } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    // Company Details
    companyName: '',
    companyDomain: '',
    industry: '',
    contactEmail: '',
    contactPhone: '',
    
    // Initial User Setup
    adminFullName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    
    // Configuration
    subscriptionTier: 'trial',
    features: {
      dataProcessing: true,
      apiAccess: false,
      advancedAnalytics: false,
      customIntegrations: false
    },
    dataRetentionDays: 90
  });

  // Redirect if not platform admin
  if (!loading && userProfile?.role !== 'platform_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const steps = [
    { id: 1, name: 'Company Details', icon: Building2 },
    { id: 2, name: 'Initial User Setup', icon: User },
    { id: 3, name: 'Configuration', icon: Settings }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleFeatureChange = (feature, enabled) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev?.features,
        [feature]: enabled
      }
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData?.companyName || !formData?.companyDomain || !formData?.industry) {
          setError('Please fill in all required company details');
          return false;
        }
        if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/?.test(formData?.companyDomain)) {
          setError('Please enter a valid domain name');
          return false;
        }
        break;
      case 2:
        if (!formData?.adminFullName || !formData?.adminEmail || !formData?.adminPassword) {
          setError('Please fill in all required user details');
          return false;
        }
        if (formData?.adminPassword !== formData?.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        if (formData?.adminPassword?.length < 8) {
          setError('Password must be at least 8 characters long');
          return false;
        }
        break;
      case 3:
        // Configuration step validation if needed
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    setError('');

    try {
      const companyData = {
        name: formData?.companyName,
        adminEmail: formData?.adminEmail,
        adminFullName: formData?.adminFullName,
        adminPassword: formData?.adminPassword
      };

      const { data, error } = await createCompany(companyData);

      if (error) {
        throw new Error(error);
      }

      if (data?.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/platform-admin-dashboard');
        }, 3000);
      } else {
        throw new Error(data?.message || 'Failed to create company');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred while creating the company');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Company Created Successfully!</h2>
          <p className="text-gray-600 mb-4">
            {formData?.companyName} has been created and the admin user has been set up.
          </p>
          <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create New Company</h1>
          <p className="mt-2 text-gray-600">Set up a new organization with initial configuration</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => {
              const StepIcon = step?.icon;
              const isCompleted = currentStep > step?.id;
              const isCurrent = currentStep === step?.id;
              
              return (
                <div key={step?.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isCompleted ? 'bg-blue-600 border-blue-600' : isCurrent ?'border-blue-600 bg-white': 'border-gray-300 bg-white'
                  }`}>
                    {isCompleted ? (
                      <Check className="h-5 w-5 text-white" />
                    ) : (
                      <StepIcon className={`h-5 w-5 ${isCurrent ? 'text-blue-600' : 'text-gray-400'}`} />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      isCurrent ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {step?.name}
                    </p>
                  </div>
                  {index < steps?.length - 1 && (
                    <div className={`flex-1 mx-4 h-0.5 ${
                      isCompleted ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Step 1: Company Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={formData?.companyName}
                      onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Acme Corporation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company Domain *
                    </label>
                    <input
                      type="text"
                      value={formData?.companyDomain}
                      onChange={(e) => handleInputChange('companyDomain', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="acme.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry *
                    </label>
                    <select
                      value={formData?.industry}
                      onChange={(e) => handleInputChange('industry', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Industry</option>
                      <option value="technology">Technology</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="finance">Finance</option>
                      <option value="retail">Retail</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={formData?.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contact@acme.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={formData?.contactPhone}
                      onChange={(e) => handleInputChange('contactPhone', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Initial User Setup */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Initial User Setup</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData?.adminFullName}
                      onChange={(e) => handleInputChange('adminFullName', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Email *
                    </label>
                    <input
                      type="email"
                      value={formData?.adminEmail}
                      onChange={(e) => handleInputChange('adminEmail', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="john@acme.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Temporary Password *
                    </label>
                    <input
                      type="password"
                      value={formData?.adminPassword}
                      onChange={(e) => handleInputChange('adminPassword', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Minimum 8 characters"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Confirm password"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <p className="text-sm text-blue-800">
                    The admin user will receive a welcome email with login instructions and will be prompted to change their password on first login.
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Configuration */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Configuration</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Tier
                    </label>
                    <select
                      value={formData?.subscriptionTier}
                      onChange={(e) => handleInputChange('subscriptionTier', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="trial">Trial (30 days)</option>
                      <option value="basic">Basic</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Retention (days)
                    </label>
                    <select
                      value={formData?.dataRetentionDays}
                      onChange={(e) => handleInputChange('dataRetentionDays', parseInt(e?.target?.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                      <option value={180}>180 days</option>
                      <option value={365}>1 year</option>
                      <option value={-1}>Unlimited</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Feature Access
                  </label>
                  <div className="space-y-3">
                    {Object.entries(formData?.features)?.map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center">
                        <input
                          type="checkbox"
                          id={feature}
                          checked={enabled}
                          onChange={(e) => handleFeatureChange(feature, e?.target?.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={feature} className="ml-2 text-sm text-gray-700">
                          {feature?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <p className="text-sm text-yellow-800">
                    These settings can be modified later through the company administration panel.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                currentStep === 1
                  ? 'text-gray-400 cursor-not-allowed' :'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </button>

            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Create Company
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}